const observerCbs = [];
let mainMtObserver = null;
/**
 *
 * @param selector CSS selector for statically rendered elements
 * @param initEachFn Callback for initialization. Is given the same arguments as Array.prototype.map and Array.prototype.forEach functions
 * @param options Advanced configuration
 */
export function initElements(selector, initEachFn, options) {
    const args = Object.assign({}, {
        observe: false,
        observerFilterFn: null,
    }, options);
    const root = typeof selector === "object" ? selector.root : document;
    const cssSelector = typeof selector === "object" ? selector.selector : selector;
    if (args.observe) {
        maybeInitMainMutationObserver();
        if (mainMtObserver) {
            observerCbs.push((mutation => {
                ([].slice.apply(mutation.addedNodes).filter((node) => {
                    if (node instanceof HTMLElement) {
                        if (typeof selector === 'object') {
                            return node.contains(selector.root) && node.matches(cssSelector);
                        }
                        else {
                            return node.matches(cssSelector);
                        }
                    }
                    return false;
                })).forEach(initEachFn);
            }));
        }
    }
    return [].slice.apply(root.querySelectorAll(cssSelector)).map(initEachFn);
}
function maybeInitMainMutationObserver() {
    if (!mainMtObserver && "MutationObserver" in window) {
        mainMtObserver = new MutationObserver(mutations => mutations.forEach(mutation => {
            observerCbs.forEach(cb => cb(mutation));
        }));
        mainMtObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
}
export function onDocumentReady(cb) {
    const checker = () => {
        const ready = document.readyState !== 'loading';
        if (ready) {
            document.removeEventListener('readystatechange', checker);
            cb();
        }
        return ready;
    };
    if (!checker()) {
        document.addEventListener('readystatechange', checker);
    }
}
export function createToggle(args) {
    const { activate, deactivate, isActive } = args;
    return {
        toggle() {
            isActive() ? deactivate() : activate();
        },
        activate,
        deactivate,
        isActive,
    };
}
export function locateEnclosingElement(el, selector) {
    let currEl = el;
    while (currEl !== window.document.body) {
        if (currEl.matches(selector)) {
            return currEl;
        }
        else if (currEl.parentElement) {
            currEl = currEl.parentElement;
        }
        else {
            break;
        }
    }
    return null;
}
export function assert(check, message) {
    if (!check) {
        const msg = message || 'Assertion failed';
        console.warn(msg);
    }
    return check;
}
export function defined(...variables) {
    return !variables.some((p) => p === null || typeof p === 'undefined');
}
export function debounceEventListener(listener, throttle) {
    let timeout = null;
    return (event) => {
        if (timeout) {
            window.clearTimeout(timeout);
        }
        timeout = window.setTimeout(() => {
            timeout = null;
            listener(event);
        }, throttle);
    };
}
const layoutResizeListeners = new Map();
export function addLayoutResizeListener(cb, args = {}) {
    var _a;
    let windowCachedWidth = window.innerWidth;
    const wrappedCb = (event) => {
        if (windowCachedWidth !== window.innerWidth) {
            cb(event);
            windowCachedWidth = window.innerWidth;
        }
    };
    const listener = args.debounce ? debounceEventListener(wrappedCb, args.debounce) : wrappedCb;
    layoutResizeListeners.set(cb, listener);
    window.addEventListener('resize', listener);
    window.addEventListener('orientationchange', listener);
    if (!args.skipOnLoad) {
        window.addEventListener('load', listener);
    }
    (_a = document.fonts) === null || _a === void 0 ? void 0 : _a.addEventListener('loadingdone', listener);
}
export function removeLayoutResizeListener(cb) {
    var _a;
    const listener = layoutResizeListeners.get(cb);
    if (listener) {
        window.removeEventListener('resize', listener);
        window.removeEventListener('orientationchange', listener);
        window.removeEventListener('load', listener);
        (_a = document.fonts) === null || _a === void 0 ? void 0 : _a.removeEventListener('loadingdone', listener);
    }
}
export function selectFocusableElements(container) {
    return [].slice.apply((container || document.body).querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'));
}
/**
 * Traps focus within <b>container</b>, using <b>selector</b> to select focusable items.
 */
export function trapFocusInsideContainer(container) {
    const els = selectFocusableElements(container);
    const firstEl = els.length ? els[0] : null;
    const lastEl = els.length > 1 ? els[els.length - 1] : firstEl;
    let escape = null;
    const keyDownHandler = (event) => {
        if (event.key === 'Tab') {
            if (event.shiftKey && document.activeElement === firstEl) {
                event.preventDefault();
                lastEl === null || lastEl === void 0 ? void 0 : lastEl.focus();
            }
            else if (!event.shiftKey && document.activeElement === lastEl) {
                event.preventDefault();
                firstEl === null || firstEl === void 0 ? void 0 : firstEl.focus();
            }
        }
        else if (event.key === 'Escape') {
            if (escape) {
                escape();
            }
        }
    };
    return {
        enter() {
            firstEl === null || firstEl === void 0 ? void 0 : firstEl.focus();
            container.addEventListener('keydown', keyDownHandler);
        },
        escape() {
            container.addEventListener('keydown', keyDownHandler);
            if (escape) {
                escape();
            }
        },
        /**
         * Promise that is resolved after focus is explicitly escaped
         * Do not use with <b>await</b>, since focus might not be always explicitly escaped.
         * All outside interactions must be handled by caller.
         */
        waitForEscape() {
            return new Promise(resolve => {
                escape = resolve;
            });
        }
    };
}
